import { useMemo } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import { Lightbulb, Loader2 } from 'lucide-react';
import type { DeltaView } from '@/lib/period-comparison';
import { compareTicketToSite, repurchaseShare } from '@/lib/sales-insights';
import { DeltaBadge } from '@/components/metrics/delta-badge';
import { AppShareBand } from '@/components/metrics/app-share-band';
import { SalesChart } from '@/components/sales-chart';
import { PaidOrdersChart } from '@/components/paid-orders-chart';
import { AverageTicketChart } from '@/components/average-ticket-chart';
import { RepurchaseRateChart } from '@/components/repurchase-rate-chart';
import { Card, CardContent } from '@/components/ui/card';
import { ChartCardHeader } from '@/components/metrics/chart-card-header';
import { SectionHeader } from '@/components/metrics/section-header';
import { OrdersCompositionCard } from '@/components/metrics/orders-composition-card';
import { OrdersCompletionCard } from '@/components/metrics/orders-completion-card';
import { formatCurrency, formatNumber } from '@/components/metrics/formatters';
import {
  ANALYTICS_SYNC_ID,
  type SalesSectionProps,
} from '@/components/metrics/metric-sections.types';
import { computeSharedCountAxisMax } from '@/lib/metrics-sales-repurchase-buckets';
import { ORDERS_COMPLETION_SEGMENT_ORIGIN } from '@/lib/orders-drilldown';

export function SalesSection({
  salesMetrics,
  isLoadingSalesMetrics,
  currency,
  granularity,
  chartRange,
  salesChart,
  aovChart,
  repurchaseChart,
  ordersCompletion,
  isLoadingSalesChart,
  isLoadingAovChart,
  isLoadingRepurchaseChart,
  isLoadingOrdersCompletion,
  openDrilldown,
  kpis,
  previousSalesChart,
  deltas,
  comparison,
}: SalesSectionProps) {
  const { LL, locale } = useI18nContext();
  const deltaOf = (delta: DeltaView | undefined) =>
    delta ? <DeltaBadge delta={delta} comparison={comparison} /> : undefined;
  const percent1 = (ratio: number) =>
    new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(ratio);

  const ticketComparison =
    salesMetrics && aovChart
      ? compareTicketToSite(
          salesMetrics.totalPaidOrders > 0 ? salesMetrics.aov : 0,
          aovChart.points.map((point) => point.aovWeb)
        )
      : null;
  const ticketText = ticketComparison
    ? (ticketComparison.direction === 'higher'
        ? LL.metricsPage.salesTab.ticketHigher
        : LL.metricsPage.salesTab.ticketLower)({
        pct: percent1(ticketComparison.ratio),
        site: formatCurrency(ticketComparison.siteTicket, locale, currency),
      })
    : null;
  const repurchaseRatio = salesMetrics ? repurchaseShare(salesMetrics) : null;
  const analyticsSyncId = ANALYTICS_SYNC_ID;

  const salesPoints = salesChart?.points ?? [];
  const repurchasePoints = repurchaseChart?.points ?? [];
  const sharedCountAxisMax = useMemo(
    () =>
      computeSharedCountAxisMax(
        salesPoints,
        repurchasePoints,
        granularity,
        locale
      ),
    [granularity, locale, salesPoints, repurchasePoints]
  );

  const syncSalesAndRepurchaseCountAxis =
    !isLoadingSalesChart && salesChart != null && !isLoadingRepurchaseChart;

  const repurchaseTotalCount =
    salesMetrics != null
      ? salesMetrics.ordersComposition.inAppRepurchases.count +
        salesMetrics.ordersComposition.channelMigrationRepurchases.count
      : undefined;

  return (
    <div>
      <SectionHeader
        title={LL.metricsPage.sections.sales.title()}
        description={LL.metricsPage.sections.sales.description()}
      />
      {kpis}

      <div className="mt-8 flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <ChartCardHeader
              title={LL.monthlySalesRevenue()}
              total={formatCurrency(
                salesMetrics?.totalRevenue,
                locale,
                currency
              )}
              helpKey="salesChart"
              onTotalClick={
                openDrilldown ? () => openDrilldown('revenue') : undefined
              }
              totalAriaLabel={LL.metricsPage.repurchaseDrilldown.viewOrdersAriaLabel(
                { label: LL.monthlySalesRevenue() }
              )}
              delta={deltaOf(deltas?.revenue)}
            />
            <CardContent>
              <div className="flex flex-col gap-4 w-full h-[200px]">
                {isLoadingSalesChart || !salesChart ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : (
                  <SalesChart
                    range={chartRange}
                    currency={currency}
                    granularity={granularity}
                    points={salesChart.points}
                    previousPoints={previousSalesChart?.points}
                    syncId={analyticsSyncId}
                    syncMethod="value"
                    onBucketClick={
                      openDrilldown
                        ? (bucketStart) => openDrilldown('revenue', bucketStart)
                        : undefined
                    }
                  />
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <ChartCardHeader
              title={LL.metricsPage.charts.averageTicket.title()}
              total={formatCurrency(salesMetrics?.aov, locale, currency)}
              helpKey="averageTicketChart"
              onTotalClick={
                openDrilldown
                  ? () => openDrilldown('average_ticket')
                  : undefined
              }
              totalAriaLabel={LL.metricsPage.repurchaseDrilldown.viewOrdersAriaLabel(
                { label: LL.metricsPage.charts.averageTicket.title() }
              )}
              delta={deltaOf(deltas?.averageTicket)}
            />
            <CardContent>
              <div className="flex flex-col gap-4 w-full h-[200px]">
                {isLoadingAovChart ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : (
                  <AverageTicketChart
                    range={chartRange}
                    currency={currency}
                    granularity={granularity}
                    points={aovChart?.points ?? []}
                    syncId={analyticsSyncId}
                    syncMethod="value"
                    onBucketClick={
                      openDrilldown
                        ? (bucketStart) =>
                            openDrilldown('average_ticket', bucketStart)
                        : undefined
                    }
                  />
                )}
              </div>
              {ticketText ? (
                <p
                  data-ticket-insight
                  className="mt-4 flex items-start gap-2.5 rounded-md bg-muted px-3 py-2.5 text-[13px] leading-snug"
                >
                  <Lightbulb
                    aria-hidden="true"
                    className="mt-0.5 size-4 shrink-0 text-[#155DFC]"
                  />
                  <span>{ticketText}</span>
                </p>
              ) : null}
            </CardContent>
          </Card>
        </div>

        {/* Between Ticket médio and Número de vendas (ANLT-04, BACK-04). */}
        <AppShareBand
          salesMetrics={salesMetrics}
          delta={deltas?.appShare ?? { kind: 'none' }}
          comparison={comparison}
          revenueChart={salesChart}
          range={chartRange}
          granularity={granularity}
          currency={currency}
        />

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <ChartCardHeader
              title={LL.totalSales()}
              total={formatNumber(salesMetrics?.totalPaidOrders, locale)}
              helpKey="salesChart"
              onTotalClick={
                openDrilldown ? () => openDrilldown('paid_orders') : undefined
              }
              totalAriaLabel={LL.metricsPage.repurchaseDrilldown.viewOrdersAriaLabel(
                { label: LL.totalSales() }
              )}
              delta={deltaOf(deltas?.orders)}
            />
            <CardContent>
              <div className="flex flex-col gap-4 w-full h-[200px]">
                {isLoadingSalesChart || !salesChart ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : (
                  <PaidOrdersChart
                    range={chartRange}
                    granularity={granularity}
                    points={salesChart.points}
                    countAxisMax={
                      syncSalesAndRepurchaseCountAxis
                        ? sharedCountAxisMax
                        : undefined
                    }
                    syncId={analyticsSyncId}
                    syncMethod="value"
                    onBucketClick={
                      openDrilldown
                        ? (bucketStart) =>
                            openDrilldown('paid_orders', bucketStart)
                        : undefined
                    }
                  />
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <ChartCardHeader
              title={LL.metricsPage.repurchases.title()}
              total={formatNumber(repurchaseTotalCount, locale)}
              helpKey="repurchaseRateChart"
              onTotalClick={
                openDrilldown
                  ? () => openDrilldown('total_repurchase')
                  : undefined
              }
              totalAriaLabel={LL.metricsPage.repurchaseDrilldown.viewOrdersAriaLabel(
                { label: LL.metricsPage.repurchases.title() }
              )}
              delta={deltaOf(deltas?.repurchases)}
              description={
                repurchaseRatio === null
                  ? undefined
                  : LL.metricsPage.salesTab.repurchaseShare({
                      pct: percent1(repurchaseRatio),
                    })
              }
            />
            <CardContent>
              <div className="flex flex-col gap-4 w-full h-[200px]">
                {isLoadingRepurchaseChart ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : (
                  <RepurchaseRateChart
                    range={chartRange}
                    granularity={granularity}
                    points={repurchaseChart?.points ?? []}
                    currency={currency}
                    countAxisMax={
                      syncSalesAndRepurchaseCountAxis
                        ? sharedCountAxisMax
                        : undefined
                    }
                    syncId={analyticsSyncId}
                    syncMethod="value"
                    onBucketClick={
                      openDrilldown
                        ? (bucketStart) =>
                            openDrilldown('total_repurchase', bucketStart)
                        : undefined
                    }
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className="md:col-span-1">
            <OrdersCompositionCard
              firstPurchaseCount={
                salesMetrics?.ordersComposition.firstPurchases.count ?? 0
              }
              firstPurchaseRevenue={
                salesMetrics?.ordersComposition.firstPurchases.revenue ?? 0
              }
              inAppRepurchaseCount={
                salesMetrics?.ordersComposition.inAppRepurchases.count ?? 0
              }
              inAppRepurchaseRevenue={
                salesMetrics?.ordersComposition.inAppRepurchases.revenue ?? 0
              }
              channelMigrationCount={
                salesMetrics?.ordersComposition.channelMigrationRepurchases
                  .count ?? 0
              }
              channelMigrationRevenue={
                salesMetrics?.ordersComposition.channelMigrationRepurchases
                  .revenue ?? 0
              }
              currency={currency}
              isLoading={isLoadingSalesMetrics}
              onSegmentClick={openDrilldown}
            />
          </div>
          <div className="md:col-span-1">
            <OrdersCompletionCard
              completedCount={ordersCompletion?.completedCount ?? 0}
              notCompletedCount={ordersCompletion?.notCompletedCount ?? 0}
              isLoading={isLoadingOrdersCompletion}
              onSegmentClick={
                openDrilldown
                  ? (segment) =>
                      openDrilldown(ORDERS_COMPLETION_SEGMENT_ORIGIN[segment])
                  : undefined
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
